import { BaseStyles, Button, Checkbox, FormControl, ThemeProvider } from '@primer/react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Modal } from '../components/modal/Modal';
import { customTheme } from '../theme/custom-theme';
import {
  applyViewSettingsProfile,
  setViewSetting,
  setViewSettingsModalOpen,
} from '../../../untidy/redux/actions/actions';
import { getAllViewSettings, getViewSettingsModalOpen } from '../../../untidy/redux/selectors';
import {
  isViewSettingOn,
  resolveProfileEnabledKeys,
  VIEW_SETTING_GROUPS,
  VIEW_SETTING_PROFILES,
  VIEW_SETTINGS,
  ViewSettingDefinition,
  ViewSettingGroup,
  ViewSettingOverrides,
  getViewSettingsForGroup,
} from './registry';

/**
 * Per-feature "View settings" modal (PLT-4824). Replaces the previous
 * `/internal/view-settings` page so toggles can be edited without leaving
 * the current page. Mounted once at the app root and toggled via redux
 * (`setViewSettingsModalOpen`) so every entry point — the user-menu link,
 * the status-bar item, and the `Ctrl+*` global hotkey — uses the same
 * action.
 *
 * The settings persist across sessions via localStorage. The
 * `useViewSetting` hook applies a team-user gate at the read site, so a
 * stale flag on a non-team account is a no-op. The modal itself is mounted
 * unconditionally — opening it is gated by the entry points (which already
 * check team membership) and by the redux flag, which starts `false` and
 * is never flipped without an explicit user action.
 */
export function ViewSettingsModal() {
  const dispatch = useDispatch();
  const isOpen = useSelector(getViewSettingsModalOpen);
  const flags = useSelector(getAllViewSettings);

  const enabledCount = VIEW_SETTINGS.filter(s => isViewSettingOn(flags, s.key)).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        dispatch(setViewSettingsModalOpen(false));
      }}
      maxInlineSize="56rem"
      maxBlockSize="90vh"
      aria-label="View settings"
    >
      {/* The Modal portals to document.body, escaping the app-level
          ThemeProvider/BaseStyles wrapper. Since PLT-6150 the Modal itself re-declares
          data-color-mode/data-light-theme, so the *colour* variables now resolve inside every
          portal without this wrapper. It stays for the rest of what BaseStyles does — it emits
          real declarations (font-family, font-size, line-height, color), not just custom
          properties, and this modal's typography depends on them. Do not read it as the fix for
          unpainted Primer controls any more; that is in Modal.tsx. */}
      <ThemeProvider theme={customTheme}>
        <BaseStyles>
          <Root>
            <Header>
              <Title>View settings</Title>
              <Description>
                Per-feature toggles for internal "expert mode" affordances. Replaces the legacy single Expert Mode
                switch. Settings persist in localStorage in this browser; nothing is sent to the server. Non-team users
                see all features off regardless of these toggles.
              </Description>
              <Toolbar>
                <ToolbarStatus>
                  {enabledCount === 0
                    ? 'All features off'
                    : `${enabledCount} feature${enabledCount === 1 ? '' : 's'} on`}
                </ToolbarStatus>
                <Profiles>
                  <ProfilesLabel>Profiles:</ProfilesLabel>
                  {VIEW_SETTING_PROFILES.map(profile => (
                    <Button
                      key={profile.id}
                      variant="default"
                      size="small"
                      title={profile.description}
                      onClick={() => {
                        dispatch(applyViewSettingsProfile(resolveProfileEnabledKeys(profile)));
                      }}
                    >
                      {profile.label}
                    </Button>
                  ))}
                </Profiles>
              </Toolbar>
            </Header>
            <Groups>
              {VIEW_SETTING_GROUPS.map(group => (
                <GroupSection key={group.id} group={group} flags={flags} />
              ))}
            </Groups>
          </Root>
        </BaseStyles>
      </ThemeProvider>
    </Modal>
  );
}

function GroupSection({ group, flags }: { group: ViewSettingGroup; flags: ViewSettingOverrides }) {
  const dispatch = useDispatch();
  const settings = getViewSettingsForGroup(group.id);
  const allOn = settings.every(s => isViewSettingOn(flags, s.key));
  const allOff = settings.every(s => !isViewSettingOn(flags, s.key));

  return (
    <Section>
      <SectionHeader>
        <SectionTitleStack>
          <SectionTitle>{group.label}</SectionTitle>
          <SectionDescription>{group.description}</SectionDescription>
        </SectionTitleStack>
        <SectionActions>
          <Button
            size="small"
            variant="default"
            disabled={allOn}
            onClick={() => {
              for (const setting of settings) {
                dispatch(setViewSetting(setting.key, true));
              }
            }}
          >
            Enable all
          </Button>
          <Button
            size="small"
            variant="default"
            disabled={allOff}
            onClick={() => {
              for (const setting of settings) {
                dispatch(setViewSetting(setting.key, false));
              }
            }}
          >
            Disable all
          </Button>
        </SectionActions>
      </SectionHeader>
      <Settings>
        {settings.map(setting => (
          <SettingRow key={setting.key} setting={setting} enabled={isViewSettingOn(flags, setting.key)} />
        ))}
      </Settings>
    </Section>
  );
}

function SettingRow({ setting, enabled }: { setting: ViewSettingDefinition; enabled: boolean }) {
  const dispatch = useDispatch();
  return (
    <FormControl>
      <Checkbox
        checked={enabled}
        onChange={e => {
          const next = (e.target as HTMLInputElement).checked;
          dispatch(setViewSetting(setting.key, next));
        }}
      />
      <FormControl.Label>{setting.label}</FormControl.Label>
      <FormControl.Caption>{setting.description}</FormControl.Caption>
      <FormControl.Caption>
        <SettingKey>{setting.key}</SettingKey>
      </FormControl.Caption>
    </FormControl>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
  /* Leave room for the absolute-positioned close button rendered by Modal. */
  padding-inline-end: var(--space__xl);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
`;

const Title = styled.h2`
  font-size: var(--font-size__xl);
  font-weight: 600;
`;

const Description = styled.p`
  color: var(--platform-gray-700);
  max-width: 60em;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space__lg);
`;

const ToolbarStatus = styled.span`
  color: var(--platform-gray-700);
`;

const Profiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space__sm);
`;

const ProfilesLabel = styled.span`
  color: var(--platform-gray-700);
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

const Section = styled.section`
  padding: var(--space__lg);
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__md);
  background-color: var(--color__gray-50);
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

const SectionHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space__lg);
`;

const SectionTitleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-size__lg);
  font-weight: 600;
  color: var(--platform-gray-900);
`;

const SectionDescription = styled.p`
  color: var(--platform-gray-700);
  max-width: 60em;
`;

const SectionActions = styled.div`
  display: flex;
  gap: var(--space__sm);
  flex-shrink: 0;
`;

const Settings = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
`;

const SettingKey = styled.code`
  font-family: var(--font-family__mono, monospace);
  color: var(--platform-gray-500);
  font-size: 0.85em;
`;
